import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface ConfigEntry<T = unknown> {
  value: T;
  status: boolean;
}

export interface ConfigurationData {
  [key: string]: ConfigEntry;
}

export interface ConfigurationResponse {
  message: string;
  data: ConfigurationData;
}

const DEFAULT_FOOTER_LOGO_PATH = '../../../../assets/logo/KronosLogo-A.png';

@Injectable({
  providedIn: 'root',
})
export class ConfigurationService {
  private http = inject(HttpClient);
  private readonly config = signal<ConfigurationData | null>(null);

  /** Load configuration from API. Call at app init. */
  load(): Promise<ConfigurationData> {
    return firstValueFrom(
      this.http.get<ConfigurationResponse>(`${environment.SERVER_URL}/configuration`)
    ).then((res) => {
      const data = res?.data ?? {};
      this.config.set(data);
      return data;
    }).catch((err) => {
      console.warn('Failed to load configuration:', err);
      this.config.set({});
      return {};
    });
  }

  /** Get the default footer logo path for Fire TV. Uses FIRETV_DEFAULT_FOOTER_LOGO from config or fallback. */
  getDefaultFooterLogo(): string {
    const data = this.config();
    const entry = data?.['FIRETV_DEFAULT_FOOTER_LOGO'] as ConfigEntry<string> | undefined;
    if (entry?.status && entry?.value) {
      const value = String(entry.value).trim();
      // If value looks like a filename only, prepend assets path
      if (!value.startsWith('/') && !value.startsWith('.') && !value.includes('assets/')) {
        return `../../../../assets/logo/${value}`;
      }
      return value;
    }
    return DEFAULT_FOOTER_LOGO_PATH;
  }

  /** Raw config for other consumers. */
  getConfig(): ConfigurationData | null {
    return this.config();
  }

  /** Get venue settings by venue ID. Returns an observable; subscribe to get data or handle errors. */
  getVenueSettings(venueId: string): Observable<{ data?: unknown }> {
    return this.http.get<{ data?: unknown }>(`${environment.SERVER_URL}/venue/${venueId}/venue-setting`);
  }
}
